/**
 * Hook pour gérer les heures supplémentaires d'un chantier
 * @module hooks/useHeuresSup
 */

import { useState, useEffect, useCallback, useMemo } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import type {
  HeureSup,
  HeureSupAvecMembre,
  CreateHeureSupData,
  UpdateHeureSupData,
  HeuresSupParMembre,
  UseHeuresSupReturn,
  HeuresSupSettings,
  DEFAULT_HEURES_SUP_SETTINGS,
  UseHeuresSupSettingsReturn,
} from '@/types/heures-sup.types';

// =============================================================================
// Hook useHeuresSup - Gestion des heures sup d'un chantier
// =============================================================================

/**
 * Hook pour gérer les heures supplémentaires d'un chantier
 * @param chantierId - ID du chantier
 */
export function useHeuresSup(chantierId: string): UseHeuresSupReturn {
  const { toast } = useToast();
  const [heuresSup, setHeuresSup] = useState<HeureSupAvecMembre[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  // Charger les heures sup du chantier
  const fetchHeuresSup = useCallback(async () => {
    if (!chantierId) {
      setHeuresSup([]);
      setIsLoading(false);
      return;
    }

    try {
      setIsLoading(true);
      setError(null);

      const { data, error: fetchError } = await supabase
        .from('heures_supplementaires')
        .select(`
          *,
          membre:membres_equipe(id, nom, prenom, taux_horaire)
        `)
        .eq('chantier_id', chantierId)
        .order('date_travail', { ascending: false });

      if (fetchError) throw fetchError;

      setHeuresSup(data || []);
    } catch (err) {
      console.error('[useHeuresSup] Erreur chargement:', err);
      setError(err instanceof Error ? err : new Error('Erreur de chargement'));
      setHeuresSup([]);
    } finally {
      setIsLoading(false);
    }
  }, [chantierId]);

  // Charger au montage et quand chantierId change
  useEffect(() => {
    fetchHeuresSup();
  }, [fetchHeuresSup]);

  // Calculer les totaux
  const totalHeures = useMemo(() => {
    return heuresSup.reduce((sum, hs) => sum + (hs.nombre_heures || 0), 0);
  }, [heuresSup]);

  const totalCout = useMemo(() => {
    return heuresSup.reduce((sum, hs) => sum + (hs.cout_total || 0), 0);
  }, [heuresSup]);

  // Calculer les heures par membre
  const heuresParMembre = useMemo((): HeuresSupParMembre[] => {
    const parMembreMap = new Map<string, HeuresSupParMembre>();

    heuresSup.forEach((hs) => {
      const membreId = hs.membre_id;
      const existing = parMembreMap.get(membreId);

      if (existing) {
        existing.total_heures += hs.nombre_heures || 0;
        existing.total_cout += hs.cout_total || 0;
      } else {
        parMembreMap.set(membreId, {
          membre_id: membreId,
          membre_nom: hs.membre?.nom || 'Inconnu',
          membre_prenom: hs.membre?.prenom || '',
          total_heures: hs.nombre_heures || 0,
          total_cout: hs.cout_total || 0,
        });
      }
    });

    return Array.from(parMembreMap.values()).sort(
      (a, b) => b.total_heures - a.total_heures
    );
  }, [heuresSup]);

  // Ajouter une entrée heures sup
  const ajouterHeureSup = useCallback(
    async (data: CreateHeureSupData): Promise<void> => {
      try {
        const { error: insertError } = await supabase
          .from('heures_supplementaires')
          .insert(data);

        if (insertError) throw insertError;

        toast({
          title: 'Heures supplémentaires ajoutées',
          description: `${data.nombre_heures}h ajoutées avec succès`,
        });

        // Recharger les données
        await fetchHeuresSup();
      } catch (err) {
        console.error('[useHeuresSup] Erreur ajout:', err);
        toast({
          title: 'Erreur',
          description: 'Impossible d\'ajouter les heures supplémentaires',
          variant: 'destructive',
        });
        throw err;
      }
    },
    [fetchHeuresSup, toast]
  );

  // Modifier une entrée heures sup
  const modifierHeureSup = useCallback(
    async (id: string, data: UpdateHeureSupData): Promise<void> => {
      try {
        const { error: updateError } = await supabase
          .from('heures_supplementaires')
          .update(data)
          .eq('id', id);

        if (updateError) throw updateError;

        toast({
          title: 'Heures supplémentaires modifiées',
          description: 'Modification enregistrée avec succès',
        });

        // Recharger les données
        await fetchHeuresSup();
      } catch (err) {
        console.error('[useHeuresSup] Erreur modification:', err);
        toast({
          title: 'Erreur',
          description: 'Impossible de modifier les heures supplémentaires',
          variant: 'destructive',
        });
        throw err;
      }
    },
    [fetchHeuresSup, toast]
  );

  // Supprimer une entrée heures sup
  const supprimerHeureSup = useCallback(
    async (id: string): Promise<void> => {
      try {
        const { error: deleteError } = await supabase
          .from('heures_supplementaires')
          .delete()
          .eq('id', id);

        if (deleteError) throw deleteError;

        toast({
          title: 'Heures supplémentaires supprimées',
          description: 'Suppression effectuée avec succès',
        });

        // Recharger les données
        await fetchHeuresSup();
      } catch (err) {
        console.error('[useHeuresSup] Erreur suppression:', err);
        toast({
          title: 'Erreur',
          description: 'Impossible de supprimer les heures supplémentaires',
          variant: 'destructive',
        });
        throw err;
      }
    },
    [fetchHeuresSup, toast]
  );

  return {
    heuresSup,
    isLoading,
    error,
    totalHeures,
    totalCout,
    heuresParMembre,
    ajouterHeureSup,
    modifierHeureSup,
    supprimerHeureSup,
    refetch: fetchHeuresSup,
  };
}

// =============================================================================
// Hook useHeuresSupSettings - Gestion des paramètres entreprise
// =============================================================================

/**
 * Hook pour gérer les paramètres heures sup d'une entreprise
 * @param entrepriseId - ID de l'entreprise
 */
export function useHeuresSupSettings(
  entrepriseId: string | null
): UseHeuresSupSettingsReturn {
  const { toast } = useToast();
  const [settings, setSettings] = useState<HeuresSupSettings>({
    heures_sup_actives: false,
    heures_sup_base_journaliere: 8,
    heures_sup_mode: 'standard',
    heures_sup_tranches: [
      { de: 36, a: 43, majoration: 25 },
      { de: 44, a: 999, majoration: 50 },
    ],
    heures_sup_nuit_actives: false,
    heures_sup_nuit_majoration: 100,
    heures_sup_nuit_debut: '21:00',
    heures_sup_nuit_fin: '06:00',
    heures_sup_ferie_actives: false,
    heures_sup_ferie_majoration: 100,
  });
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  // Charger les settings
  const fetchSettings = useCallback(async () => {
    if (!entrepriseId) {
      setIsLoading(false);
      return;
    }

    try {
      setIsLoading(true);
      setError(null);

      const { data, error: fetchError } = await supabase
        .from('entreprises')
        .select(`
          heures_sup_actives,
          heures_sup_base_journaliere,
          heures_sup_mode,
          heures_sup_tranches,
          heures_sup_nuit_actives,
          heures_sup_nuit_majoration,
          heures_sup_nuit_debut,
          heures_sup_nuit_fin,
          heures_sup_ferie_actives,
          heures_sup_ferie_majoration
        `)
        .eq('id', entrepriseId)
        .single();

      if (fetchError) throw fetchError;

      if (data) {
        setSettings({
          heures_sup_actives: data.heures_sup_actives ?? false,
          heures_sup_base_journaliere: data.heures_sup_base_journaliere ?? 8,
          heures_sup_mode: data.heures_sup_mode ?? 'standard',
          heures_sup_tranches: data.heures_sup_tranches ?? [
            { de: 36, a: 43, majoration: 25 },
            { de: 44, a: 999, majoration: 50 },
          ],
          heures_sup_nuit_actives: data.heures_sup_nuit_actives ?? false,
          heures_sup_nuit_majoration: data.heures_sup_nuit_majoration ?? 100,
          heures_sup_nuit_debut: data.heures_sup_nuit_debut ?? '21:00',
          heures_sup_nuit_fin: data.heures_sup_nuit_fin ?? '06:00',
          heures_sup_ferie_actives: data.heures_sup_ferie_actives ?? false,
          heures_sup_ferie_majoration: data.heures_sup_ferie_majoration ?? 100,
        });
      }
    } catch (err) {
      console.error('[useHeuresSupSettings] Erreur chargement:', err);
      setError(err instanceof Error ? err : new Error('Erreur de chargement'));
    } finally {
      setIsLoading(false);
    }
  }, [entrepriseId]);

  // Charger au montage
  useEffect(() => {
    fetchSettings();
  }, [fetchSettings]);

  // Mettre à jour les settings
  const updateSettings = useCallback(
    async (newSettings: Partial<HeuresSupSettings>): Promise<void> => {
      if (!entrepriseId) return;

      try {
        const { error: updateError } = await supabase
          .from('entreprises')
          .update(newSettings)
          .eq('id', entrepriseId);

        if (updateError) throw updateError;

        // Mettre à jour l'état local
        setSettings((prev) => ({ ...prev, ...newSettings }));

        toast({
          title: 'Paramètres enregistrés',
          description: 'Les paramètres heures supplémentaires ont été mis à jour',
        });
      } catch (err) {
        console.error('[useHeuresSupSettings] Erreur update:', err);
        toast({
          title: 'Erreur',
          description: 'Impossible de mettre à jour les paramètres',
          variant: 'destructive',
        });
        throw err;
      }
    },
    [entrepriseId, toast]
  );

  return {
    settings,
    isLoading,
    error,
    updateSettings,
    refetch: fetchSettings,
  };
}

// =============================================================================
// Hook useHeuresSupTotals - Récupérer les totaux via RPC
// =============================================================================

/**
 * Hook pour récupérer les totaux heures sup d'un chantier via RPC
 * Plus léger que useHeuresSup si on n'a besoin que des totaux
 */
export function useHeuresSupTotals(chantierId: string | null) {
  const [totals, setTotals] = useState<{
    total_heures: number;
    total_cout: number;
    count_entries: number;
  } | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const fetchTotals = useCallback(async () => {
    if (!chantierId) {
      setTotals(null);
      setIsLoading(false);
      return;
    }

    try {
      setIsLoading(true);

      const { data, error } = await supabase.rpc('get_heures_sup_totals', {
        p_chantier_id: chantierId,
      });

      if (error) throw error;

      if (data && data.length > 0) {
        setTotals(data[0]);
      } else {
        setTotals({
          total_heures: 0,
          total_cout: 0,
          count_entries: 0,
        });
      }
    } catch (err) {
      console.error('[useHeuresSupTotals] Erreur:', err);
      setTotals(null);
    } finally {
      setIsLoading(false);
    }
  }, [chantierId]);

  useEffect(() => {
    fetchTotals();
  }, [fetchTotals]);

  return {
    totals,
    isLoading,
    refetch: fetchTotals,
  };
}
